import { Anchor, Badge, Stack, Table, Tabs, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, useInfinitePage, useTableSort } from "../components/ui";
import { disputesApi } from "../lib/api";
import { useQueryFilters } from "../lib/useQueryFilters";
import { NAV_ICONS } from "../lib/navCopy";
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE_LABELS,
  disputeStatusColor,
  formatDate,
} from "../lib/format";
import { axioTokens } from "../theme";

interface Props {
  basePath?: string;
}

const DISPUTE_TABS = [
  { value: "open", label: "Open" },
  { value: "investigating", label: "Investigating" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
] as const;

const DISPUTE_TAB_DEFAULT = DISPUTE_TABS[0].value;
const DISPUTE_FILTER_DEFAULTS = { tab: DISPUTE_TAB_DEFAULT };
const DISPUTE_FILTER_ALLOWED = { tab: DISPUTE_TABS.map((item) => item.value) };
const DISPUTE_FILTER_OPTIONS = { allowed: DISPUTE_FILTER_ALLOWED };

export function DisputesPage({ basePath = "/disputes" }: Props) {
  const disputeFilters = useQueryFilters(DISPUTE_FILTER_DEFAULTS, DISPUTE_FILTER_OPTIONS);
  const tab = disputeFilters.values.tab;

  const statusFilter = tab === "closed" ? "closed" : tab === "all" ? undefined : tab;

  const disputesQuery = useInfinitePage(["disputes", statusFilter], (page, pageSize) =>
    disputesApi.list({ status: statusFilter, page, page_size: pageSize }),
  );

  const items = disputesQuery.items;

  const { sorted, sortKey, direction, toggle } = useTableSort(items, {
    type: (r) => (DISPUTE_TYPE_LABELS[r.dispute_type] ?? r.dispute_type).toLowerCase(),
    status: (r) => r.status,
    candidateJob: (r) => `${r.context?.candidate_name ?? ""} ${r.context?.job_title ?? ""}`.toLowerCase(),
    opened: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.disputes}
        title="Disputes"
        subtitle="Track issues you raised or that involve you. AxioHire mediates as a neutral party."
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && disputeFilters.set({ tab: value })}>
          <Tabs.List>
            {DISPUTE_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={disputesQuery.isLoading}
        hasMore={disputesQuery.hasMore}
        loadingMore={disputesQuery.loadingMore}
        onLoadMore={disputesQuery.loadMore}
        isEmpty={items.length === 0}
        empty={{
          icon: NAV_ICONS.disputes,
          title: "No disputes",
          description: "No disputes in this category.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="type" sortKey={sortKey} direction={direction} onSort={toggle}>
                Type
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="candidateJob" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate / Job
              </SortableTh>
              <SortableTh columnKey="opened" sortKey={sortKey} direction={direction} onSort={toggle}>
                Opened
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>{DISPUTE_TYPE_LABELS[d.dispute_type] ?? d.dispute_type}</Table.Td>
                <Table.Td>
                  <Badge color={disputeStatusColor(d.status)} variant="light">
                    {DISPUTE_STATUS_LABELS[d.status] ?? d.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {d.context?.candidate_name ?? "—"} / {d.context?.job_title ?? "—"}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" className="tabular">
                    {formatDate(d.created_at)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Anchor
                    component={Link}
                    to={`${basePath}/${d.id}`}
                    size="sm"
                    fw="var(--axio-font-weight-semibold)"
                    c={axioTokens.ink}
                  >
                    View
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
